import React from "react";

// Luxury casino wheel (reference-image style): outer pocket ring with gold
// dividers, large polished gold cone centre, gold hub with yellow bearing,
// green zero wedge extending over the cone, gold numbers, pearl ball.

export const GOLD = "#b8933a";

export const LUXE_FILL = { red: "url(#luxe-red)", black: "url(#luxe-black)", green: "url(#luxe-green)" };

export const LuxeDefs = ({ cx, cy, r }) => (
  <defs>
    <linearGradient id="luxe-gold" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor="#f3e3a5" />
      <stop offset="0.25" stopColor="#d3ae47" />
      <stop offset="0.5" stopColor="#8a651a" />
      <stop offset="0.75" stopColor="#e3c76e" />
      <stop offset="1" stopColor="#6e5013" />
    </linearGradient>
    <radialGradient id="luxe-cone" cx="0.42" cy="0.36" r="0.95">
      <stop offset="0" stopColor="#f6e7ae" />
      <stop offset="0.35" stopColor="#e0c168" />
      <stop offset="0.68" stopColor="#c29b3a" />
      <stop offset="1" stopColor="#8a651a" />
    </radialGradient>
    <radialGradient id="luxe-hub" cx="0.4" cy="0.32" r="0.9">
      <stop offset="0" stopColor="#f9efc6" />
      <stop offset="0.5" stopColor="#ddbc5c" />
      <stop offset="1" stopColor="#8a651a" />
    </radialGradient>
    <radialGradient id="luxe-bearing" cx="0.4" cy="0.3" r="0.9">
      <stop offset="0" stopColor="#fff6b8" />
      <stop offset="0.55" stopColor="#f0cd3d" />
      <stop offset="1" stopColor="#9c7513" />
    </radialGradient>
    <radialGradient id="luxe-red" gradientUnits="userSpaceOnUse" cx={cx} cy={cy} r={r}>
      <stop offset="0" stopColor="#4a060f" />
      <stop offset="0.72" stopColor="#8a0d1e" />
      <stop offset="0.88" stopColor="#b0142b" />
      <stop offset="1" stopColor="#560812" />
    </radialGradient>
    <radialGradient id="luxe-black" gradientUnits="userSpaceOnUse" cx={cx} cy={cy} r={r}>
      <stop offset="0" stopColor="#0a0a0e" />
      <stop offset="0.72" stopColor="#17171f" />
      <stop offset="0.88" stopColor="#262630" />
      <stop offset="1" stopColor="#060608" />
    </radialGradient>
    <radialGradient id="luxe-green" gradientUnits="userSpaceOnUse" cx={cx} cy={cy} r={r}>
      <stop offset="0" stopColor="#046b32" />
      <stop offset="0.72" stopColor="#0a8a42" />
      <stop offset="0.88" stopColor="#12a352" />
      <stop offset="1" stopColor="#04572a" />
    </radialGradient>
  </defs>
);

const d2r = (d) => (d * Math.PI) / 180;
const pt = (cx, cy, r, a) => `${(cx + r * Math.sin(a)).toFixed(2)},${(cy - r * Math.cos(a)).toFixed(2)}`;

function ringSeg(cx, cy, i, seg, r0, r1) {
  const a0 = d2r(i * seg - seg / 2), a1 = d2r(i * seg + seg / 2);
  return `M${pt(cx, cy, r1, a0)} A${r1},${r1} 0 0 1 ${pt(cx, cy, r1, a1)} L${pt(cx, cy, r0, a1)} A${r0},${r0} 0 0 0 ${pt(cx, cy, r0, a0)} Z`;
}

export function LuxeWheel({ viewBox, width, height, cx, cy, r, pockets, labelFontSize = 12, rotateLabels = true, testId, style }) {
  const n = pockets.length, seg = 360 / n;
  const r1 = r, r0 = r * 0.7, hubR = r * 0.145, hubRing = r * 0.24;
  const labelR = (r0 + r1) / 2;
  return (
    <svg viewBox={viewBox} width={width} height={height} data-testid={testId} style={style}>
      <LuxeDefs cx={cx} cy={cy} r={r} />
      <circle cx={cx} cy={cy} r={r1 + 6} fill="url(#luxe-gold)" />
      <circle cx={cx} cy={cy} r={r1 + 3.5} fill="none" stroke="url(#luxe-gold)" strokeWidth="4.5" />
      {pockets.map((p, i) => (
        <path key={`s${i}`} d={ringSeg(cx, cy, i, seg, r0, r1)} fill={LUXE_FILL[p.color]} stroke="url(#luxe-gold)" strokeWidth="1.5" />
      ))}
      <circle cx={cx} cy={cy} r={r1} fill="none" stroke="url(#luxe-gold)" strokeWidth="1.5" />
      {/* polished gold cone centre */}
      <circle cx={cx} cy={cy} r={r0} fill="url(#luxe-cone)" stroke="url(#luxe-gold)" strokeWidth="2.5" />
      {/* brushed-metal light sweeps on the cone */}
      <path d={`M${cx},${cy} L${pt(cx, cy, r0, d2r(-52))} A${r0},${r0} 0 0 1 ${pt(cx, cy, r0, d2r(-14))} Z`} fill="rgba(255,255,255,0.16)" />
      <path d={`M${cx},${cy} L${pt(cx, cy, r0, d2r(128))} A${r0},${r0} 0 0 1 ${pt(cx, cy, r0, d2r(168))} Z`} fill="rgba(0,0,0,0.12)" />
      {/* green zero wedge extending over the cone */}
      {pockets.map((p, i) =>
        p.color === "green" ? (
          <path key={`g${i}`} d={ringSeg(cx, cy, i, seg, hubRing, r0 + 1)} fill="url(#luxe-green)" stroke="url(#luxe-gold)" strokeWidth="1.2" />
        ) : null
      )}
      {/* hub + yellow bearing */}
      <circle cx={cx} cy={cy} r={hubRing} fill="none" stroke="rgba(110,80,19,0.6)" strokeWidth="1.6" />
      <circle cx={cx} cy={cy} r={hubR + r * 0.045} fill="url(#luxe-hub)" stroke="#8a651a" strokeWidth="1" />
      <circle cx={cx} cy={cy} r={hubR * 0.62} fill="url(#luxe-bearing)" stroke="#7c5c14" strokeWidth="0.8" />
      {/* numbers */}
      {pockets.map((p, i) => {
        const a = d2r(i * seg);
        const x = cx + labelR * Math.sin(a), y = cy - labelR * Math.cos(a);
        return (
          <text key={`t${i}`} x={x} y={y} fill="#ecd9a0" fontSize={labelFontSize} fontWeight="800"
            textAnchor="middle" dominantBaseline="central"
            transform={rotateLabels ? `rotate(${(i * seg).toFixed(2)}, ${x.toFixed(2)}, ${y.toFixed(2)})` : undefined}>
            {p.label}
          </text>
        );
      })}
    </svg>
  );
}

// Static (non-rotating) studio-light gloss over the wheel.
export const WheelSheen = () => (
  <div
    className="absolute inset-0 rounded-full pointer-events-none z-10"
    style={{
      background:
        "radial-gradient(ellipse 58% 38% at 32% 20%, rgba(255,255,255,0.14), rgba(255,255,255,0.04) 45%, transparent 65%), radial-gradient(ellipse 40% 26% at 74% 84%, rgba(255,255,255,0.05), transparent 60%)",
    }}
  />
);

export const PearlBall = ({ size = 16 }) => (
  <div
    style={{
      position: "relative",
      height: size,
      width: size,
      borderRadius: "50%",
      background:
        "radial-gradient(circle at 30% 25%, #ffffff 0%, #fdfcf7 30%, #f0ecdf 52%, #d8d2c2 72%, #b3ab97 100%)",
      boxShadow:
        "0 2px 6px rgba(0,0,0,0.55), inset -2px -3px 6px rgba(140,120,90,0.35), inset 1px 2px 3px rgba(255,255,255,0.95), 0 0 12px rgba(255,250,235,0.8), 0 0 4px rgba(255,255,255,0.9)",
    }}
  >
    {/* iridescent pearl lustre */}
    <span
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        background:
          "radial-gradient(circle at 68% 70%, rgba(255,182,193,0.28) 0%, transparent 45%), radial-gradient(circle at 25% 68%, rgba(173,216,230,0.25) 0%, transparent 45%), radial-gradient(circle at 70% 25%, rgba(221,190,255,0.2) 0%, transparent 40%)",
      }}
    />
    {/* sharp specular glint */}
    <span
      style={{
        position: "absolute",
        top: "14%",
        left: "22%",
        height: "30%",
        width: "30%",
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.35) 55%, transparent 75%)",
      }}
    />
  </div>
);
